import CheckIcon from '@mui/icons-material/Check';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Button from '@mui/material/Button';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import { useState } from 'react';

import { ROLE_LABELS } from '@/auth/roles';
import { useAuth } from '@/auth/useAuth';
import { useViewingAs } from '@/auth/useViewingAs';

/**
 * Which organization the portal is currently showing, and a way to change it.
 *
 * Only a button when somebody belongs to more than one. With a single
 * organization there is nothing to switch between, so it stays the plain
 * label it has always been rather than a menu that opens onto one item.
 *
 * Switching is more than a label change: the person's role travels with the
 * organization, so the menu shows the role held in each and the navigation
 * changes with the choice.
 */
export function OrganizationSwitcher() {
  const { user, switchOrganization } = useAuth();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const { viewing } = useViewingAs();

  // Staff viewing an organization see its name and nothing to switch to: they
  // belong to none, and the way out is the banner's Exit.
  const organizations = viewing ? [] : (user?.organizations ?? []);
  const current = viewing ? viewing.name : (user?.organizationName ?? '');

  if (organizations.length <= 1) {
    return (
      <Typography
        variant="body2"
        color="text.secondary"
        noWrap
        sx={{ display: { xs: 'none', sm: 'block' } }}
      >
        {current}
      </Typography>
    );
  }

  const choose = (organizationId: string) => {
    switchOrganization(organizationId);
    setAnchor(null);
  };

  return (
    <>
      <Button
        size="small"
        color="inherit"
        onClick={(event) => setAnchor(event.currentTarget)}
        endIcon={<ExpandMoreIcon />}
        aria-haspopup="menu"
        aria-expanded={anchor ? true : undefined}
        aria-label={`Current organization: ${current}. Switch organization`}
        sx={{
          display: { xs: 'none', sm: 'inline-flex' },
          color: 'text.secondary',
          textTransform: 'none',
          fontWeight: 400,
        }}
      >
        {current}
      </Button>

      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        slotProps={{ list: { 'aria-label': 'Your organizations' } }}
      >
        {organizations.map((organization) => (
          <MenuItem
            key={organization.id}
            selected={organization.id === user?.organizationId}
            onClick={() => choose(organization.id)}
          >
            <ListItemIcon>
              {organization.id === user?.organizationId ? (
                <CheckIcon fontSize="small" />
              ) : null}
            </ListItemIcon>
            <ListItemText
              primary={organization.name}
              secondary={ROLE_LABELS[organization.role]}
            />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
